import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Download, FileText, Trash2, LibraryBig, ArrowLeft, ArrowRight,
  Check, HelpCircle, Sparkles, X, Pencil,
} from 'lucide-react';
import api, { API, getToken } from '../lib/api';
import { INDUSTRIAS } from '../lib/constants';
import { useAuth } from '../lib/auth';
import HelpDot from '../components/HelpDot';
import QuotaBanner from '../components/QuotaBanner';
import UpgradeRequestModal from '../components/UpgradeRequestModal';
import { useQuotaUsage } from '../lib/useQuotaUsage';
import MissionShell from '../components/MissionShell';
import { MISSIONS } from '../lib/missions';

/* ------------------------------------------------------------------ */
/*  Constants: definiciones amigables para el usuario no especialista */
/* ------------------------------------------------------------------ */

const EMPTY = {
  name: '', area: 'Operaciones', purpose: '', legal_basis: '',
  data_categories: [], data_subjects: [], recipients: [],
  international_transfers: '', retention_period: '',
  security_measures: [], notes: '',
};

// Ejemplos de nombres por industria (paso 1)
const NAME_EXAMPLES = {
  retail:     ['Programa de fidelización', 'Registro de ventas', 'Cámaras de seguridad'],
  salud:      ['Ficha clínica', 'Reserva de horas', 'Facturación a Isapres'],
  fintech:    ['Onboarding y KYC', 'Procesamiento de transacciones', 'Evaluación crediticia'],
  educacion:  ['Matrícula de estudiantes', 'Comunicación con apoderados', 'Gestión de becas'],
  ecommerce:  ['Compras online', 'Newsletter', 'Recuperación de carritos'],
  servicios:  ['Base de clientes B2B', 'Newsletter comercial', 'Soporte a clientes'],
  tecnologia: ['Cuentas de usuario', 'Analítica de producto', 'Soporte técnico'],
  industria:  ['Nómina y RRHH', 'Control de acceso', 'Proveedores'],
  otro:       ['Gestión de clientes', 'Marketing', 'Personal'],
};

// Base legal explicada (paso 3)
const LEGAL_BASIS_EXPLAINED = [
  {
    value: 'consentimiento',
    label: 'Consentimiento del titular',
    short: 'La persona aceptó expresamente.',
    example: 'Marketing por correo: "acepto recibir promociones".',
    when: 'Marketing, cookies no esenciales, encuestas voluntarias.',
  },
  {
    value: 'contrato',
    label: 'Ejecución de un contrato',
    short: 'Los datos son necesarios para cumplir un contrato con esa persona.',
    example: 'Enviar un pedido: necesito su dirección.',
    when: 'Ventas, entregas, prestación de servicios contratados.',
  },
  {
    value: 'obligacion_legal',
    label: 'Obligación legal',
    short: 'La ley chilena exige que los recopiles.',
    example: 'Emitir boleta al SII, previsión social.',
    when: 'Facturación, laboral, salud (ficha clínica), tributario.',
  },
  {
    value: 'interes_legitimo',
    label: 'Interés legítimo',
    short: 'Un interés razonable del negocio, sin perjudicar al titular.',
    example: 'Cámaras de seguridad para prevenir robos.',
    when: 'Seguridad, prevención de fraude, comunicaciones B2B básicas.',
  },
  {
    value: 'interes_vital',
    label: 'Interés vital',
    short: 'Proteger la vida o salud de una persona.',
    example: 'Historial de alergias en emergencia médica.',
    when: 'Urgencias médicas, situaciones excepcionales.',
  },
  {
    value: 'interes_publico',
    label: 'Interés público',
    short: 'Función pública o interés general.',
    example: 'Trámites municipales, estadísticas oficiales.',
    when: 'Organismos públicos, funciones delegadas por ley.',
  },
];

// Categorías de datos explicadas (paso 4)
const DATA_CATEGORIES_EXPLAINED = [
  { value: 'identificacion',   label: 'Identificación',           example: 'Nombre, RUT, fecha de nacimiento' },
  { value: 'contacto',         label: 'Contacto',                 example: 'Teléfono, correo, dirección' },
  { value: 'laboral',          label: 'Laboral',                  example: 'Cargo, AFP, historial laboral' },
  { value: 'financiera',       label: 'Financiera',               example: 'Ingresos, tarjeta, cuenta bancaria' },
  { value: 'salud',            label: 'Salud',                    example: 'Diagnósticos, medicamentos, alergias' },
  { value: 'biometrica',       label: 'Biométrica',               example: 'Huella, reconocimiento facial, video' },
  { value: 'socioeconomica',   label: 'Socioeconómica',           example: 'Nivel de estudios, situación familiar' },
  { value: 'geolocalizacion',  label: 'Ubicación',                example: 'GPS, IP, dirección exacta' },
  { value: 'conducta',         label: 'Comportamiento',           example: 'Historial de compras, clics, preferencias' },
  { value: 'sensible',         label: 'Sensible (otras)',         example: 'Ideología, religión, orientación sexual' },
  { value: 'otra',             label: 'Otra',                     example: 'Cualquier dato personal no listado' },
];

// Titulares explicados (paso 4)
const DATA_SUBJECTS_EXPLAINED = [
  { value: 'clientes',    label: 'Clientes',    hint: 'Personas que compran o contratan tus servicios' },
  { value: 'empleados',   label: 'Empleados',   hint: 'Colaboradores con contrato de trabajo' },
  { value: 'candidatos',  label: 'Candidatos',  hint: 'Postulantes a empleos' },
  { value: 'proveedores', label: 'Proveedores', hint: 'Contactos de empresas que te venden' },
  { value: 'visitantes',  label: 'Visitantes',  hint: 'Personas que ingresan a tus instalaciones o sitio' },
  { value: 'menores',     label: 'Menores',     hint: 'Menores de 14 años (requieren consentimiento del apoderado)' },
];

// Retenciones sugeridas (paso 5)
const RETENTION_OPTIONS = [
  '6 años (obligación tributaria)',
  '10 años tras término de contrato (laboral)',
  '15 años (ficha clínica – Res. 3722 MINSAL)',
  '5 años tras término de la relación',
  '30 días (cámaras de seguridad)',
  'Hasta que el titular retire su consentimiento',
  'Duración de la relación + 1 año',
];

// Medidas de seguridad checklist (paso 6)
const SECURITY_MEASURES_CHECKLIST = [
  'Control de acceso por rol',
  'Cifrado en reposo',
  'Cifrado en tránsito (TLS/HTTPS)',
  'Doble factor de autenticación',
  'Respaldos periódicos',
  'Bitácora de accesos',
  'Anonimización o seudonimización',
  'Contratos con encargados (DPA)',
  'Capacitación al personal',
  'Política de contraseñas',
];

const AREA_EXAMPLES = {
  RRHH:         'Contratos, remuneraciones, evaluaciones',
  Marketing:    'Newsletter, redes sociales, publicidad',
  Ventas:       'CRM, facturación, atención comercial',
  TI:           'Cuentas de usuario, logs, backups',
  Operaciones:  'Producción, logística, cámaras',
  Legal:        'Contratos, reclamos, cumplimiento',
  Finanzas:     'Contabilidad, cobros, pagos',
};

// Sugerencias de base legal por finalidad (heurístico simple para paso 3)
function suggestLegalBasis(purpose = '') {
  const p = purpose.toLowerCase();
  if (/marketing|promoci|newsletter|fideliz|encuesta/.test(p)) return 'consentimiento';
  if (/factur|tribut|sii|previsi|afp|isapre|fonasa|obligaci/.test(p)) return 'obligacion_legal';
  if (/c[aá]mara|seguridad|fraude|prevenci/.test(p)) return 'interes_legitimo';
  if (/contrato|entreg|env[ií]o|servicio contratado|pedido|venta/.test(p)) return 'contrato';
  return null;
}

/* ------------------------------------------------------------------ */
/*  Página principal RAT                                              */
/* ------------------------------------------------------------------ */

export default function RAT() {
  const { organization } = useAuth();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [systemsCount, setSystemsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { data, mode: 'wizard' | 'expert' }
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [industryChoice, setIndustryChoice] = useState(organization?.industria || 'servicios');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { usage, refresh: refreshUsage } = useQuotaUsage();

  const handleQuotaError = (e) => {
    const d = e?.response?.data?.detail;
    if (d && typeof d === 'object' && d.error === 'quota_exceeded') {
      toast.error(d.message || 'Alcanzó el tope del plan.');
      setShowUpgrade(true);
      return true;
    }
    return false;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [m, list, sys] = await Promise.all([api.get('/rat/meta'), api.get('/rat/activities'), api.get('/systems')]);
      setMeta(m.data); setItems(list.data); setSystemsCount(sys.data.length);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadTemplates = async (ind) => {
    const { data } = await api.get(`/rat/templates?industry=${ind}`);
    setTemplates(data);
  };

  const applyTemplate = async () => {
    try {
      const { data } = await api.post('/rat/apply-template', { industry: industryChoice, template_ids: 'all' });
      toast.success(`${data.inserted} actividades agregadas`);
      setShowTemplates(false);
      await load();
      await refreshUsage();
    } catch (e) {
      if (!handleQuotaError(e)) toast.error(e?.response?.data?.detail || 'Error');
    }
  };

  const save = async (payload, id) => {
    try {
      if (id) await api.put(`/rat/activities/${id}`, payload);
      else await api.post('/rat/activities', payload);
      toast.success(id ? 'Actividad actualizada' : 'Actividad registrada');
      setEditing(null);
      await load();
      await refreshUsage();
    } catch (e) {
      if (!handleQuotaError(e)) toast.error(e?.response?.data?.detail || 'Error');
    }
  };

  const del = async (id) => {
    if (!window.confirm('¿Eliminar esta actividad?')) return;
    await api.delete(`/rat/activities/${id}`);
    toast.success('Actividad eliminada');
    await load();
  };

  const download = async (fmt) => {
    const res = await fetch(`${API}/rat/export/${fmt}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) return toast.error('Error al exportar');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `RAT_Priviax.${fmt === 'xlsx' ? 'xlsx' : 'pdf'}`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-sm text-[#6B7280]">Cargando RAT…</div>;

  const withRetention = items.filter((a) => a.retention_period).length;
  const withSecurity = items.filter((a) => (a.security_measures || []).length > 0).length;

  return (
    <MissionShell
      mission={MISSIONS.rat}
      idPrefix="rat-mission"
      prereq={systemsCount === 0 ? { message: 'Antes de registrar actividades, registra tus sistemas y proveedores: cada actividad debe indicar dónde viven sus datos.', to: '/app/sistemas', cta: 'Ir a Sistemas y proveedores' } : null}
      doHint={items.length === 0
        ? 'Siguiente acción: parte por «Cargar plantilla» de tu industria y ajusta cada actividad con el asistente guiado.'
        : 'Mantén el registro al día: cada actividad debe tener base legal, plazo de retención y medidas de seguridad.'}
      evidence={[
        { label: 'Actividades registradas', value: items.length, tone: items.length ? 'ok' : 'warn', detail: 'Con finalidad, base legal y responsable' },
        { label: 'Con plazo de retención', value: `${withRetention} de ${items.length}`, tone: items.length && withRetention < items.length ? 'warn' : 'ok' },
        { label: 'Con medidas de seguridad', value: `${withSecurity} de ${items.length}`, tone: items.length && withSecurity < items.length ? 'warn' : 'ok' },
      ]}
      evidenceNote="Exporta el PDF o Excel y súbelo a tu Carpeta de Fiscalización: es el documento que presentas ante la Agencia."
    >
    <div className="space-y-6" data-testid="rat-page">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E5E7EB] pb-5">
        <div>
          <div className="text-[15px] font-medium text-[#1C2329]">
            {items.length} actividad{items.length === 1 ? '' : 'es'} registrada{items.length === 1 ? '' : 's'}
          </div>
          <div className="text-[13px] text-[#6B7280]">Registro de tratamientos exigido por la Ley N.º 21.719</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="pv-btn-secondary flex items-center gap-2 text-[13px]" onClick={() => download('pdf')} data-testid="rat-export-pdf">
            <Download size={13} /> PDF
          </button>
          <button className="pv-btn-secondary flex items-center gap-2 text-[13px]" onClick={() => download('xlsx')} data-testid="rat-export-xlsx">
            <Download size={13} /> Excel
          </button>
          <button className="pv-btn-secondary flex items-center gap-2 text-[13px]" onClick={() => { setShowTemplates(true); loadTemplates(industryChoice); }} data-testid="rat-open-templates">
            <LibraryBig size={13} /> Cargar plantilla
          </button>
          <button className="pv-btn-primary flex items-center gap-2" onClick={() => setEditing({ data: { ...EMPTY }, mode: 'wizard' })} data-testid="rat-new">
            <Plus size={14} /> Nueva actividad
          </button>
        </div>
      </div>

      <QuotaBanner usage={usage} quotaKey="rats" onRequestUpgrade={() => setShowUpgrade(true)} />
      <UpgradeRequestModal open={showUpgrade} onClose={(sent) => { setShowUpgrade(false); if (sent) refreshUsage(); }} />

      {items.length === 0 ? (
        <div className="border border-[#E5E7EB] bg-white p-10 text-center" data-testid="rat-empty">
          <FileText size={26} className="mx-auto text-[#9CA3AF]" strokeWidth={1.5} />
          <div className="mt-3 text-lg font-semibold text-[#1C2329]">Aún no hay actividades registradas</div>
          <div className="text-sm text-[#6B7280] mt-1">Puede empezar con una plantilla por industria o crear una manualmente con el asistente guiado.</div>
        </div>
      ) : (
        <div className="border border-[#E5E7EB] bg-white overflow-x-auto">
          <table className="pv-table" data-testid="rat-table">
            <thead><tr>
              <th>Actividad</th><th>Área</th><th>Base legal</th>
              <th>Titulares</th><th>Retención</th><th style={{ width: 130 }}></th>
            </tr></thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} data-testid={`rat-row-${a.id}`}>
                  <td>
                    <button onClick={() => setEditing({ data: a, mode: 'wizard' })} className="text-left">
                      <div className="font-medium text-[#1C2329] hover:text-[#143A34]">{a.name}</div>
                      <div className="text-[12px] text-[#6B7280] mt-0.5 line-clamp-1">{a.purpose}</div>
                    </button>
                  </td>
                  <td><span className="pv-chip"><span className="pv-chip-dot" />{a.area}</span></td>
                  <td className="text-[13px]">{meta?.legal_basis?.[a.legal_basis] || a.legal_basis || '—'}</td>
                  <td className="text-[13px]">{(a.data_subjects || []).join(', ')}</td>
                  <td className="text-[12px] text-[#6B7280]">{a.retention_period}</td>
                  <td>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditing({ data: a, mode: 'expert' })}
                        className="text-[#6B7280] hover:text-[#1C2329] hover:bg-[#F3F4F6] p-1 rounded"
                        title="Editar rápido (modo experto)"
                        data-testid={`rat-edit-expert-${a.id}`}>
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => del(a.id)} className="text-[#DC2626] hover:bg-[#FEF2F2] p-1 rounded" data-testid={`rat-delete-${a.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ActivityEditor
          activity={editing.data}
          initialMode={editing.mode}
          meta={meta}
          industry={organization?.industria || 'servicios'}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}

      {showTemplates && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={() => setShowTemplates(false)}>
          <div className="bg-white border border-[#E5E7EB] max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="rat-templates-modal">
            <div className="p-6 border-b border-[#E5E7EB]">
              <div className="text-[15px] font-semibold text-[#1C2329]">Cargar plantillas por industria</div>
              <select className="pv-input mt-3" value={industryChoice} onChange={(e) => { setIndustryChoice(e.target.value); loadTemplates(e.target.value); }} data-testid="tpl-industry">
                {INDUSTRIAS.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
              </select>
            </div>
            <div className="p-6 space-y-3">
              {templates.length === 0 ? <div className="text-sm text-[#6B7280]">Sin plantillas para esta industria.</div> :
                templates.map((t) => (
                  <div key={t.id} className="border border-[#E5E7EB] p-3">
                    <div className="font-medium text-[#1C2329]">{t.name}</div>
                    <div className="text-[12px] text-[#6B7280] mt-1">{t.purpose}</div>
                  </div>
                ))
              }
            </div>
            <div className="p-6 border-t border-[#E5E7EB] flex justify-between">
              <button className="pv-btn-secondary" onClick={() => setShowTemplates(false)}>Cancelar</button>
              <button className="pv-btn-primary" onClick={applyTemplate} disabled={!templates.length} data-testid="tpl-apply">
                Agregar {templates.length} actividad{templates.length === 1 ? '' : 'es'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </MissionShell>
  );
}

/* ------------------------------------------------------------------ */
/*  Editor: wizard (novatos) + modo experto (formulario compacto)     */
/* ------------------------------------------------------------------ */

function ActivityEditor({ activity, initialMode, meta, industry, onSave, onCancel }) {
  const [form, setForm] = useState({ ...EMPTY, ...activity });
  const [mode, setMode] = useState(initialMode || 'wizard');
  const [systems, setSystems] = useState([]);
  const isEdit = !!activity?.id;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/systems');
        if (!cancelled) setSystems(data);
      } catch (_e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={onCancel}>
      <div
        className="bg-white border border-[#E5E7EB] max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="rat-editor"
      >
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <div className="text-[15px] font-semibold text-[#1C2329]">
              {isEdit ? 'Editar actividad' : 'Nueva actividad de tratamiento'}
            </div>
            <div className="text-[12px] text-[#6B7280] mt-0.5">
              {mode === 'wizard'
                ? 'Te acompañamos paso a paso con preguntas en lenguaje simple.'
                : 'Formulario compacto para usuarios avanzados.'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center border border-[#E5E7EB] bg-[#F9FAFB] p-0.5 rounded-sm text-[12px]" data-testid="rat-mode-toggle">
              <button
                className={`px-2.5 py-1 rounded-sm transition-colors ${mode === 'wizard' ? 'bg-white text-[#1C2329] shadow-sm' : 'text-[#6B7280]'}`}
                onClick={() => setMode('wizard')}
                data-testid="rat-mode-wizard">
                Asistente
              </button>
              <button
                className={`px-2.5 py-1 rounded-sm transition-colors ${mode === 'expert' ? 'bg-white text-[#1C2329] shadow-sm' : 'text-[#6B7280]'}`}
                onClick={() => setMode('expert')}
                data-testid="rat-mode-expert">
                Experto
              </button>
            </div>
            <button onClick={onCancel} className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#6B7280]" aria-label="Cerrar">
              <X size={16} />
            </button>
          </div>
        </div>

        {mode === 'wizard' ? (
          <WizardBody form={form} setForm={setForm} meta={meta} industry={industry} systems={systems}
            onCancel={onCancel} onSave={() => onSave(form, form.id)} isEdit={isEdit} />
        ) : (
          <ExpertBody form={form} setForm={setForm} meta={meta} systems={systems}
            onCancel={onCancel} onSave={() => onSave(form, form.id)} />
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Wizard: 6 pasos                                                   */
/* ------------------------------------------------------------------ */

const WIZARD_STEPS = [
  { key: 'name',     title: '¿Qué actividad quieres registrar?' },
  { key: 'purpose',  title: '¿Para qué se usan estos datos?' },
  { key: 'legal',    title: '¿Con qué justificación legal?' },
  { key: 'data',     title: '¿Qué información recolectas y de quiénes?' },
  { key: 'sharing',  title: '¿Con quién los compartes y por cuánto tiempo?' },
  { key: 'security', title: '¿Cómo los proteges? Revisemos el resumen' },
];

function WizardBody({ form, setForm, meta, industry, systems, onCancel, onSave, isEdit }) {
  const [step, setStep] = useState(0);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const toggleArr = (k, val) => setForm((f) => ({
    ...f, [k]: (f[k] || []).includes(val) ? f[k].filter((x) => x !== val) : [...(f[k] || []), val],
  }));

  const canNext = useMemo(() => {
    switch (step) {
      case 0: return form.name.trim().length > 2 && !!form.area;
      case 1: return form.purpose.trim().length > 5;
      case 2: return !!form.legal_basis;
      case 3: return form.data_categories.length > 0 && form.data_subjects.length > 0;
      case 4: return !!form.retention_period && form.retention_period.trim().length > 0;
      case 5: return true;
      default: return true;
    }
  }, [step, form]);

  const isLast = step === WIZARD_STEPS.length - 1;

  return (
    <>
      {/* Stepper */}
      <div className="px-6 py-3 border-b border-[#E5E7EB] bg-[#FAFBFC]">
        <div className="flex items-center gap-1" data-testid="rat-stepper">
          {WIZARD_STEPS.map((s, i) => {
            const done = i < step;
            const active = i === step;
            const clickable = isEdit || i <= step;
            return (
              <button
                key={s.key}
                disabled={!clickable}
                onClick={() => clickable && setStep(i)}
                className={`flex-1 group flex items-center gap-2 py-1.5 text-left ${clickable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                data-testid={`rat-step-${i}`}
              >
                <div className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold border transition-colors ${
                  done ? 'bg-[#143A34] border-[#143A34] text-white' :
                  active ? 'bg-white border-[#143A34] text-[#143A34]' :
                  'bg-white border-[#E5E7EB] text-[#9CA3AF]'
                }`}>
                  {done ? <Check size={12} strokeWidth={3} /> : i + 1}
                </div>
                <div className={`hidden md:block text-[11.5px] leading-tight ${active ? 'text-[#1C2329] font-medium' : done ? 'text-[#6B7280]' : 'text-[#9CA3AF]'}`}>
                  {['Actividad', 'Finalidad', 'Base legal', 'Datos', 'Compartir', 'Seguridad'][i]}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="mb-5">
          <div className="text-[10.5px] tracking-wider text-[#6B7280] font-medium">Paso {step + 1} de {WIZARD_STEPS.length}</div>
          <h3 className="text-[19px] font-semibold text-[#1C2329] mt-1 leading-snug">{WIZARD_STEPS[step].title}</h3>
        </div>

        {step === 0 && <Step1 form={form} set={set} meta={meta} industry={industry} />}
        {step === 1 && <Step2 form={form} set={set} />}
        {step === 2 && <Step3 form={form} set={set} />}
        {step === 3 && <Step4 form={form} toggleArr={toggleArr} />}
        {step === 4 && <Step5 form={form} set={set} systems={systems} toggleArr={toggleArr} />}
        {step === 5 && <Step6 form={form} set={set} toggleArr={toggleArr} meta={meta} systems={systems} onJumpTo={setStep} />}      </div>

      <div className="px-6 py-4 border-t border-[#E5E7EB] flex items-center justify-between bg-white">
        <button className="pv-btn-secondary flex items-center gap-1.5 text-[13px]"
          onClick={() => step === 0 ? onCancel() : setStep(step - 1)}
          data-testid="wizard-back">
          <ArrowLeft size={13} /> {step === 0 ? 'Cancelar' : 'Atrás'}
        </button>
        {isLast ? (
          <button className="pv-btn-primary flex items-center gap-1.5" onClick={onSave} data-testid="wizard-finish">
            <Check size={14} /> {isEdit ? 'Guardar cambios' : 'Crear actividad'}
          </button>
        ) : (
          <button className="pv-btn-primary flex items-center gap-1.5" disabled={!canNext}
            onClick={() => setStep(step + 1)} data-testid="wizard-next">
            Continuar <ArrowRight size={13} />
          </button>
        )}
      </div>
    </>
  );
}

/* ---------- Paso 1: Nombre + área --------------------------------- */
function Step1({ form, set, meta, industry }) {
  const examples = NAME_EXAMPLES[industry] || NAME_EXAMPLES.otro;
  return (
    <div className="space-y-6">
      <p className="text-[14px] text-[#374151] leading-relaxed">
        Piensa en una <b>actividad concreta</b> de tu empresa en la que uses datos personales.
        No es una lista de datos, sino un proceso: &ldquo;atender clientes&rdquo;, &ldquo;pagar sueldos&rdquo;, etc.
      </p>

      <div>
        <label className="block text-[13px] font-medium text-[#1C2329] mb-2">Nombre de la actividad</label>
        <input
          className="pv-input"
          placeholder="Ej: Programa de fidelización de clientes"
          value={form.name}
          onChange={set('name')}
          data-testid="w-name"
          autoFocus
        />
        <div className="mt-2 flex flex-wrap gap-1.5">
          <span className="text-[11px] text-[#6B7280] pt-1 pr-1">Ejemplos:</span>
          {examples.map((ex) => (
            <button key={ex} type="button" onClick={() => set('name')(ex)}
              className="text-[11.5px] px-2 py-0.5 border border-[#E5E7EB] hover:border-[#143A34] hover:text-[#143A34] text-[#6B7280] rounded-sm">
              {ex}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-[13px] font-medium text-[#1C2329] mb-2">¿Qué área de tu empresa la realiza?</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {(meta?.areas || []).map((a) => (
            <button key={a} type="button"
              onClick={() => set('area')(a)}
              className={`text-left border p-2.5 transition-colors ${
                form.area === a
                  ? 'border-[#143A34] bg-[#F0FDF4] text-[#143A34]'
                  : 'border-[#E5E7EB] hover:border-[#C7D2CE] text-[#374151]'
              }`}
              data-testid={`w-area-${a}`}>
              <div className="text-[13px] font-medium">{a}</div>
              <div className="text-[11px] text-[#6B7280] mt-0.5">{AREA_EXAMPLES[a] || ''}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Paso 2: Finalidad ------------------------------------- */
function Step2({ form, set }) {
  return (
    <div className="space-y-5">
      <p className="text-[14px] text-[#374151] leading-relaxed">
        Describe <b>por qué</b> tu empresa recolecta y usa esos datos. Sin tecnicismos.
        Basta con una frase que responda &ldquo;¿para qué sirve?&rdquo;.
      </p>

      <div>
        <label className="block text-[13px] font-medium text-[#1C2329] mb-2">Finalidad</label>
        <textarea
          className="pv-input min-h-[110px]"
          placeholder="Ej: Enviar promociones y descuentos a clientes que aceptaron recibirlos, y medir la efectividad de cada campaña."
          value={form.purpose}
          onChange={set('purpose')}
          data-testid="w-purpose"
          autoFocus
        />
        <div className="mt-2 flex items-start gap-1.5 text-[11.5px] text-[#6B7280]">
          <HelpCircle size={13} className="mt-0.5 shrink-0" />
          <span>Consejo: si necesitas usar los datos para <b>más de una cosa</b> (ej: vender + hacer marketing), es mejor crear <b>dos actividades separadas</b>.</span>
        </div>
      </div>
    </div>
  );
}

/* ---------- Paso 3: Base legal ------------------------------------ */
function Step3({ form, set }) {
  const suggested = useMemo(() => suggestLegalBasis(form.purpose), [form.purpose]);

  return (
    <div className="space-y-5">
      <p className="text-[14px] text-[#374151] leading-relaxed">
        La ley exige justificar <b>por qué puedes tratar</b> esos datos <HelpDot termKey="base_legal" size="sm" />. Elige la que mejor describa tu caso.
      </p>

      {suggested && (
        <div className="border border-[#143A34]/20 bg-[#F0FDF4] px-3 py-2 flex items-start gap-2 text-[12.5px] text-[#065F46]">
          <Sparkles size={14} className="mt-0.5 shrink-0" />
          <span>
            Por lo que describiste, la base más habitual es{' '}
            <b>{LEGAL_BASIS_EXPLAINED.find(l => l.value === suggested)?.label}</b>.
            Puedes elegir otra si tu caso es distinto.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-2">
        {LEGAL_BASIS_EXPLAINED.map((lb) => {
          const active = form.legal_basis === lb.value;
          return (
            <button key={lb.value} type="button"
              onClick={() => set('legal_basis')(lb.value)}
              className={`text-left border p-3.5 transition-colors ${
                active ? 'border-[#143A34] bg-[#F0FDF4]' : 'border-[#E5E7EB] hover:border-[#C7D2CE]'
              }`}
              data-testid={`w-legal-${lb.value}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-[13.5px] font-medium text-[#1C2329]">{lb.label}</div>
                  <div className="text-[12.5px] text-[#374151] mt-0.5">{lb.short}</div>
                  <div className="text-[11.5px] text-[#6B7280] mt-1.5">
                    <span className="text-[#9CA3AF]">Ejemplo:</span> {lb.example}
                  </div>
                  <div className="text-[11.5px] text-[#6B7280] mt-0.5">
                    <span className="text-[#9CA3AF]">Cuándo usarla:</span> {lb.when}
                  </div>
                </div>
                <div className={`shrink-0 w-4 h-4 rounded-full border-2 mt-1 ${active ? 'border-[#143A34] bg-[#143A34]' : 'border-[#D1D5DB]'}`}>
                  {active && <Check size={10} strokeWidth={4} className="text-white m-auto mt-[1px]" />}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Paso 4: Datos y titulares ----------------------------- */
function Step4({ form, toggleArr }) {
  const hasSensible = form.data_categories.includes('sensible') || form.data_categories.includes('salud') || form.data_categories.includes('biometrica');
  const hasMenores = form.data_subjects.includes('menores');

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[13px] font-medium text-[#1C2329] mb-1 flex items-center gap-1.5">
          ¿Qué tipos de información manejas? <HelpDot termKey="datos_sensibles" size="sm" />
        </div>
        <div className="text-[12px] text-[#6B7280] mb-3">Marca todas las que apliquen. Puedes tocar cada tarjeta para ver ejemplos.</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {DATA_CATEGORIES_EXPLAINED.map((c) => {
            const active = form.data_categories.includes(c.value);
            return (
              <button key={c.value} type="button" onClick={() => toggleArr('data_categories', c.value)}
                className={`text-left border p-2.5 flex items-start gap-2 transition-colors ${
                  active ? 'border-[#143A34] bg-[#F0FDF4]' : 'border-[#E5E7EB] hover:border-[#C7D2CE]'
                }`}
                data-testid={`w-cat-${c.value}`}>
                <div className={`shrink-0 w-4 h-4 border-2 mt-0.5 flex items-center justify-center ${
                  active ? 'border-[#143A34] bg-[#143A34]' : 'border-[#D1D5DB]'
                }`}>
                  {active && <Check size={10} strokeWidth={4} className="text-white" />}
                </div>
                <div>
                  <div className="text-[13px] font-medium text-[#1C2329]">{c.label}</div>
                  <div className="text-[11.5px] text-[#6B7280] mt-0.5">{c.example}</div>
                </div>
              </button>
            );
          })}
        </div>
        {hasSensible && (
          <div className="mt-3 border border-[#F59E0B]/30 bg-[#FFFBEB] px-3 py-2 text-[12px] text-[#92400E] flex items-start gap-2">
            <HelpCircle size={13} className="mt-0.5 shrink-0" />
            <span>Estás manejando <b>datos sensibles</b>. La Ley 21.719 exige medidas reforzadas y, salvo excepciones, consentimiento explícito.</span>
          </div>
        )}
      </div>

      <div>
        <div className="text-[13px] font-medium text-[#1C2329] mb-1 flex items-center gap-1.5">
          ¿De quiénes son esos datos? <HelpDot termKey="titular" size="sm" />
        </div>
        <div className="text-[12px] text-[#6B7280] mb-3">Selecciona todos los grupos de personas involucradas.</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {DATA_SUBJECTS_EXPLAINED.map((s) => {
            const active = form.data_subjects.includes(s.value);
            return (
              <button key={s.value} type="button" onClick={() => toggleArr('data_subjects', s.value)}
                className={`text-left border p-2.5 transition-colors ${
                  active ? 'border-[#143A34] bg-[#F0FDF4]' : 'border-[#E5E7EB] hover:border-[#C7D2CE]'
                }`}
                data-testid={`w-subj-${s.value}`}>
                <div className="text-[13px] font-medium text-[#1C2329]">{s.label}</div>
                <div className="text-[11.5px] text-[#6B7280] mt-0.5">{s.hint}</div>
              </button>
            );
          })}
        </div>
        {hasMenores && (
          <div className="mt-3 border border-[#F59E0B]/30 bg-[#FFFBEB] px-3 py-2 text-[12px] text-[#92400E] flex items-start gap-2">
            <HelpCircle size={13} className="mt-0.5 shrink-0" />
            <span>Datos de <b>menores de 14 años</b> requieren consentimiento del apoderado y protección reforzada.</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Paso 5: Compartir + retención ------------------------- */
function Step5({ form, set, systems, toggleArr }) {
  const [recipInput, setRecipInput] = useState('');
  const [intlEnabled, setIntlEnabled] = useState(!!form.international_transfers);

  const addRecipient = () => {
    const v = recipInput.trim();
    if (!v) return;
    if (!(form.recipients || []).includes(v)) set('recipients')([...(form.recipients || []), v]);
    setRecipInput('');
  };
  const removeRecipient = (r) => set('recipients')((form.recipients || []).filter((x) => x !== r));

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[13px] font-medium text-[#1C2329] mb-1">¿En qué sistemas o procesos viven estos datos?</div>
        <div className="text-[12px] text-[#6B7280] mb-3">
          Selecciona del catastro. Si falta alguno, agrégalo en <b>Sistemas</b> y vuelve.
        </div>
        {systems.length === 0 ? (
          <div className="border border-dashed border-[#D1D5DB] p-3 text-[12px] text-[#6B7280]">
            Aún no tienes sistemas en el catastro. Puedes continuar y agregarlos después.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {systems.map((s) => {
              const active = (form.data_system_ids || []).includes(s.id);
              return (
                <button key={s.id} type="button" onClick={() => toggleArr('data_system_ids', s.id)}
                  className={`text-left border p-2.5 flex items-start gap-2 transition-colors ${
                    active ? 'border-[#143A34] bg-[#F0FDF4]' : 'border-[#E5E7EB] hover:border-[#C7D2CE]'
                  }`}
                  data-testid={`w-sys-${s.id}`}>
                  <div className={`shrink-0 w-4 h-4 border-2 mt-0.5 flex items-center justify-center ${
                    active ? 'border-[#143A34] bg-[#143A34]' : 'border-[#D1D5DB]'
                  }`}>
                    {active && <Check size={10} strokeWidth={4} className="text-white" />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium text-[#1C2329] truncate">{s.name}</div>
                    <div className="text-[11.5px] text-[#6B7280] mt-0.5 truncate">
                      {s.provider || 'sin proveedor'} · {s.has_logs ? 'con logs' : 'sin logs propios'}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <div className="text-[13px] font-medium text-[#1C2329] mb-1">¿Con quién compartes esta información?</div>
        <div className="text-[12px] text-[#6B7280] mb-3">Áreas internas, proveedores, autoridades, etc. Presiona Enter para agregar.</div>
        <div className="flex gap-2">
          <input className="pv-input" placeholder="Ej: SII, AFP, Plataforma de email marketing"
            value={recipInput}
            onChange={(e) => setRecipInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }}
            data-testid="w-recipient-input" />
          <button type="button" onClick={addRecipient} className="pv-btn-secondary text-[13px]">Agregar</button>
        </div>
        {(form.recipients || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(form.recipients || []).map((r) => (
              <span key={r} className="inline-flex items-center gap-1 text-[12px] bg-[#F3F4F6] border border-[#E5E7EB] px-2 py-0.5">
                {r}
                <button onClick={() => removeRecipient(r)} className="text-[#6B7280] hover:text-[#DC2626]"><X size={11} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <input type="checkbox" id="intl-check" className="w-4 h-4 accent-[#143A34]"
            checked={intlEnabled}
            onChange={(e) => {
              setIntlEnabled(e.target.checked);
              if (!e.target.checked) set('international_transfers')('');
            }}
            data-testid="w-intl-check" />
          <label htmlFor="intl-check" className="text-[13px] font-medium text-[#1C2329]">
            ¿Los datos salen de Chile o van a un proveedor extranjero?
          </label>
        </div>
        {intlEnabled && (
          <input className="pv-input"
            placeholder="Ej: Proveedor de email marketing (EE.UU.), con cláusulas contractuales"
            value={form.international_transfers || ''}
            onChange={set('international_transfers')}
            data-testid="w-intl-input" />
        )}
      </div>

      <div>
        <div className="text-[13px] font-medium text-[#1C2329] mb-1">¿Por cuánto tiempo los guardas?</div>
        <div className="text-[12px] text-[#6B7280] mb-3">Elige una opción o escribe el plazo específico de tu empresa.</div>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {RETENTION_OPTIONS.map((r) => (
            <button key={r} type="button" onClick={() => set('retention_period')(r)}
              className={`text-[11.5px] px-2 py-1 border transition-colors ${
                form.retention_period === r ? 'border-[#143A34] bg-[#F0FDF4] text-[#143A34]' : 'border-[#E5E7EB] text-[#374151] hover:border-[#C7D2CE]'
              }`}
              data-testid={`w-ret-${r.slice(0, 12)}`}>
              {r}
            </button>
          ))}
        </div>
        <input className="pv-input"
          placeholder="O escribe un plazo personalizado…"
          value={form.retention_period}
          onChange={set('retention_period')}
          data-testid="w-retention" />
      </div>
    </div>
  );
}

/* ---------- Paso 6: Seguridad + resumen --------------------------- */
function Step6({ form, set, toggleArr, meta, systems, onJumpTo }) {
  const selectedSystems = (form.data_system_ids || [])
    .map((id) => systems.find((s) => s.id === id)?.name)
    .filter(Boolean);
  return (
    <div className="space-y-6">
      <div>
        <div className="text-[13px] font-medium text-[#1C2329] mb-1">¿Qué medidas de seguridad aplicas?</div>
        <div className="text-[12px] text-[#6B7280] mb-3">Marca las que ya tienes implementadas. Puedes añadir más después.</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {SECURITY_MEASURES_CHECKLIST.map((m) => {
            const active = form.security_measures.includes(m);
            return (
              <button key={m} type="button" onClick={() => toggleArr('security_measures', m)}
                className={`text-left border p-2 flex items-center gap-2 text-[12.5px] transition-colors ${
                  active ? 'border-[#143A34] bg-[#F0FDF4] text-[#143A34]' : 'border-[#E5E7EB] text-[#374151] hover:border-[#C7D2CE]'
                }`}
                data-testid={`w-sec-${m.slice(0, 12)}`}>
                <div className={`shrink-0 w-4 h-4 border-2 flex items-center justify-center ${
                  active ? 'border-[#143A34] bg-[#143A34]' : 'border-[#D1D5DB]'
                }`}>
                  {active && <Check size={10} strokeWidth={4} className="text-white" />}
                </div>
                {m}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-[13px] font-medium text-[#1C2329] mb-1">Notas internas (opcional)</div>
        <textarea className="pv-input" rows={2}
          placeholder="Cualquier detalle interno útil para tu equipo"
          value={form.notes || ''} onChange={set('notes')}
          data-testid="w-notes" />
      </div>

      <div className="border-t border-[#E5E7EB] pt-5">
        <div className="text-[13px] font-medium text-[#1C2329] mb-3">Resumen antes de guardar</div>
        <div className="border border-[#E5E7EB] bg-[#FAFBFC] divide-y divide-[#E5E7EB] text-[12.5px]" data-testid="w-summary">
          <SummaryRow label="Actividad" value={form.name || '—'} onEdit={() => onJumpTo(0)} />
          <SummaryRow label="Área" value={form.area} onEdit={() => onJumpTo(0)} />
          <SummaryRow label="Finalidad" value={form.purpose || '—'} onEdit={() => onJumpTo(1)} />
          <SummaryRow label="Base legal"
            value={meta?.legal_basis?.[form.legal_basis] || '—'} onEdit={() => onJumpTo(2)} />
          <SummaryRow label="Datos" value={form.data_categories.join(', ') || '—'} onEdit={() => onJumpTo(3)} />
          <SummaryRow label="Titulares" value={form.data_subjects.join(', ') || '—'} onEdit={() => onJumpTo(3)} />
          <SummaryRow label="Sistemas" value={selectedSystems.join(', ') || '—'} onEdit={() => onJumpTo(4)} />
          <SummaryRow label="Destinatarios" value={(form.recipients || []).join(', ') || '—'} onEdit={() => onJumpTo(4)} />
          <SummaryRow label="Transferencia intl." value={form.international_transfers || '—'} onEdit={() => onJumpTo(4)} />
          <SummaryRow label="Retención" value={form.retention_period || '—'} onEdit={() => onJumpTo(4)} />
          <SummaryRow label="Seguridad" value={(form.security_measures || []).join(', ') || '—'} onEdit={() => onJumpTo(5)} />
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, onEdit }) {
  return (
    <div className="grid grid-cols-[130px_1fr_auto] gap-3 px-3 py-2 items-start">
      <div className="text-[#6B7280]">{label}</div>
      <div className="text-[#1C2329]">{value}</div>
      <button onClick={onEdit} className="text-[11.5px] text-[#143A34] hover:underline">Editar</button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Modo experto: formulario compacto (una sola pantalla)             */
/* ------------------------------------------------------------------ */

function ExpertBody({ form, setForm, meta, systems, onCancel, onSave }) {
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const toggleArr = (k, val) => setForm((f) => ({
    ...f, [k]: (f[k] || []).includes(val) ? f[k].filter((x) => x !== val) : [...(f[k] || []), val],
  }));
  return (
    <>
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <F label="Nombre"><input className="pv-input" value={form.name} onChange={set('name')} data-testid="ex-name" /></F>
        <div className="grid grid-cols-2 gap-4">
          <F label="Área">
            <select className="pv-input" value={form.area} onChange={set('area')}>
              {(meta?.areas || []).map((a) => <option key={a}>{a}</option>)}
            </select>
          </F>
          <F label="Base legal">
            <select className="pv-input" value={form.legal_basis} onChange={set('legal_basis')}>
              <option value="">— Seleccione —</option>
              {Object.entries(meta?.legal_basis || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </F>
        </div>
        <F label="Finalidad"><textarea className="pv-input" rows={2} value={form.purpose} onChange={set('purpose')} /></F>
        <F label="Categorías de datos">
          <div className="flex flex-wrap gap-1.5">
            {DATA_CATEGORIES_EXPLAINED.map((c) => (
              <button key={c.value} type="button" onClick={() => toggleArr('data_categories', c.value)}
                className={`text-[12px] px-2 py-1 border ${form.data_categories.includes(c.value) ? 'bg-[#ECFDF5] border-[#2A9B68] text-[#065F46]' : 'border-[#E5E7EB] text-[#374151]'}`}>
                {c.label}
              </button>
            ))}
          </div>
        </F>
        <F label="Titulares">
          <div className="flex flex-wrap gap-1.5">
            {DATA_SUBJECTS_EXPLAINED.map((s) => (
              <button key={s.value} type="button" onClick={() => toggleArr('data_subjects', s.value)}
                className={`text-[12px] px-2 py-1 border ${form.data_subjects.includes(s.value) ? 'bg-[#ECFDF5] border-[#2A9B68] text-[#065F46]' : 'border-[#E5E7EB] text-[#374151]'}`}>
                {s.label}
              </button>
            ))}
          </div>
        </F>
        <F label="Sistemas (del catastro)">
          <div className="flex flex-wrap gap-1.5">
            {(systems || []).length === 0
              ? <span className="text-[12px] text-[#6B7280]">Sin sistemas registrados aún.</span>
              : (systems || []).map((s) => (
                <button key={s.id} type="button" onClick={() => toggleArr('data_system_ids', s.id)}
                  className={`text-[12px] px-2 py-1 border ${(form.data_system_ids || []).includes(s.id) ? 'bg-[#ECFDF5] border-[#2A9B68] text-[#065F46]' : 'border-[#E5E7EB] text-[#374151]'}`}>
                  {s.name}
                </button>
              ))}
          </div>
        </F>
        <F label="Destinatarios (separe con coma)">
          <input className="pv-input" value={(form.recipients || []).join(', ')}
            onChange={(e) => set('recipients')(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />
        </F>
        <div className="grid grid-cols-2 gap-4">
          <F label="Transferencia internacional"><input className="pv-input" value={form.international_transfers || ''} onChange={set('international_transfers')} /></F>
          <F label="Plazo de retención"><input className="pv-input" value={form.retention_period} onChange={set('retention_period')} /></F>
        </div>
        <F label="Medidas de seguridad (separe con coma)">
          <input className="pv-input" value={(form.security_measures || []).join(', ')}
            onChange={(e) => set('security_measures')(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />
        </F>
        <F label="Notas"><textarea className="pv-input" rows={2} value={form.notes || ''} onChange={set('notes')} /></F>
      </div>
      <div className="p-4 border-t border-[#E5E7EB] flex justify-between bg-white">
        <button className="pv-btn-secondary" onClick={onCancel}>Cancelar</button>
        <button className="pv-btn-primary" onClick={onSave} data-testid="ex-save">Guardar</button>
      </div>
    </>
  );
}

function F({ label, children }) {
  return <div><label className="block text-[12px] font-medium text-[#374151] mb-1.5">{label}</label>{children}</div>;
}
